/**
 * Before/after review shown when the user saves.
 *
 * Renders a git-style line diff (react-diff-viewer-continued) inside the shared
 * oasis-ui-kit Modal so it matches the rest of the dashboard. The user must
 * confirm here to commit the change.
 */

import { useEffect, useMemo, useRef } from "react";
import ReactDiffViewer from "react-diff-viewer-continued";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../../shared/enums/modalId";
import type { JsonObject, JsonValue } from "../types";
import { stringify } from "../utils/json";
import "./DiffModal.css";

interface DiffModalProps {
  isOpen: boolean;
  oldData: JsonObject;
  newData: JsonValue;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DiffModal = ({
  isOpen,
  oldData,
  newData,
  saving,
  onCancel,
  onConfirm,
}: DiffModalProps) => {
  const dialogId = ModalId.DOCUMENT_STUDIO_DIFF_MODAL;
  const isModalOpenRef = useRef(false);

  const oldText = useMemo(() => stringify(oldData), [oldData]);
  const newText = useMemo(() => stringify(newData), [newData]);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(dialogId);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(dialogId);
      isModalOpenRef.current = false;
    }
  }, [isOpen, dialogId]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(dialogId);
      }
    },
    [dialogId],
  );

  return (
    <Modal
      dialogId={dialogId}
      title="Review changes before saving"
      saveButtonText={saving ? "Saving..." : "Confirm & Save"}
      cancelText="Cancel"
      onSave={onConfirm}
      onCancel={onCancel}
      onClose={onCancel}
      disableSave={saving}
      heightPercentage={85}
    >
      <div className="py-2 text-sm">
        <ReactDiffViewer
          oldValue={oldText}
          newValue={newText}
          splitView
          leftTitle="Saved"
          rightTitle="Edited"
        />
      </div>
    </Modal>
  );
};

export default DiffModal;
